import { useCallback, useState } from 'react';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import getLang from 'academy-common-lib/utils/getLang';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { SearchScoringBaseConfiguration, useSearch } from 'academy-common-lib/features/search/hooks/useSearch';
import MostPopularCertificatesHeader from './MostPopularCertificatesHeader';
import { useMyLearning } from '../../features/myLearning/myLearningQueries';
import UsageTracker from '../../utils/UsageTracker';
import HomePageSection from '../Layouts/HomePageSection';
import { CardsSection } from '../../features/cardsSection/components/CardsSection';
import FormattedMessage from 'I18n/components/FormattedMessage';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import Raven from 'raven-js';
import { useSearchFeedback } from '../../features/search/searchQueries';
import { getSearchUrl } from '../../features/search/searchRouting';
import { NavMarker } from 'react-rhumb';
import { jsx as _jsx } from "react/jsx-runtime";
export const useMostPopularCertificates = () => {
  const language = getLang();
  const {
    myLearningData
  } = useMyLearning({
    variables: {
      contentTypes: ['TRACK'],
      progressType: 'COMPLETED',
      hasCertification: true,
      offset: {
        cursor: 0
      }
    }
  });
  const numCompletedCertificates = myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.total;
  const {
    searchResults,
    searchResultsLoading,
    correlationId,
    totalCount
  } = useSearch({
    variables: {
      contentTypes: ['TRACK'],
      hasAwardsOnly: true,
      languages: [language.toUpperCase()],
      pageLength: 12,
      offset: {
        cursor: 0
      },
      shouldFilterDeprecated: true,
      featuredHomepageOnly: true,
      featuredCertificationsOnly: true
    },
    scoringConfig: Object.assign({}, SearchScoringBaseConfiguration, {
      includeRecencyDecay: false
    })
  });
  const certificateIds = (searchResults === null || searchResults === void 0 ? void 0 : searchResults.map(result => result.id)) || [];
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: certificateIds.map(id => {
      var _searchResults$find;
      return {
        id,
        type: 'TRACK',
        language: language,
        resultMLOptionId: searchResults === null || searchResults === void 0 || (_searchResults$find = searchResults.find(result => result.id === id)) === null || _searchResults$find === void 0 ? void 0 : _searchResults$find.resultMLOptionId
      };
    }),
    skip: !certificateIds.length
  });
  const activeCertificates = (cardModels || []).filter(certificate => {
    var _certificate$progress;
    return ((_certificate$progress = certificate.progress) === null || _certificate$progress === void 0 ? void 0 : _certificate$progress.status) !== 'COMPLETED';
  });
  return {
    certificates: activeCertificates,
    certificatesLoading: cardModelsLoading || searchResultsLoading,
    certificatesError: cardModelsError,
    numCompletedCertificates,
    correlationId,
    totalSearchResults: totalCount
  };
};
const MostPopularCertificates = () => {
  const {
    isTablet,
    isDesktop
  } = useMediaQuery();
  const [currentPage, setCurrentPage] = useState(0);
  const [maxCards, setMaxCards] = useState();
  const {
    certificates,
    numCompletedCertificates,
    certificatesLoading,
    certificatesError,
    correlationId
  } = useMostPopularCertificates();
  const [sendSearchFeedback] = useSearchFeedback();
  const isPaginated = isDesktop && maxCards !== undefined;
  const startIndex = isPaginated ? currentPage * maxCards : 0;
  const endIndex = isPaginated ? startIndex + maxCards : !isDesktop ? Math.min(4, certificates.length) : certificates.length;
  const displayedCertificates = certificates.slice(startIndex, endIndex);
  const hasMore = isPaginated && endIndex < certificates.length;
  const showPrevious = isPaginated && currentPage > 0;
  const handleClickNext = useCallback(() => {
    setCurrentPage(cp => cp + 1);
  }, []);
  const handleClickPrevious = useCallback(() => {
    setCurrentPage(cp => cp - 1);
  }, []);
  const handleOnItemClick = useCallback((item, __index) => {
    var _item$progress;
    if (correlationId && item.resultMLOptionId) {
      sendSearchFeedback({
        variables: {
          correlationId,
          contentContainerType: 'TRACK',
          resultMLOptionId: item.resultMLOptionId
        }
      }).catch(Raven.captureException.bind(Raven));
    }
    const positionInFullList = startIndex + __index + 1;
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'content-card-click',
      contentType: 'TRACK',
      trackId: item.id,
      appearsIn: 'popular-certificates',
      position: positionInFullList,
      name: item.name,
      style: 'list',
      isInProgress: ((_item$progress = item.progress) === null || _item$progress === void 0 ? void 0 : _item$progress.status) === 'IN_PROGRESS',
      isCertification: true
    });
  }, [correlationId, sendSearchFeedback, startIndex]);
  if (!certificatesLoading && !certificatesError && !certificates.length) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "HOMEPAGE_POPULAR_CERTIFICATES_ERROR"
    });
  }
  return /*#__PURE__*/_jsx(HomePageSection, {
    paddingBlock: "0",
    children: /*#__PURE__*/_jsx(CardsSection, {
      appearsIn: "certificates",
      cardsContainerProps: {
        paddingBlockEnd: '0'
      },
      content: displayedCertificates,
      direction: isTablet ? 'column' : 'row',
      error: certificatesError,
      errorMarker: "HOMEPAGE_POPULAR_CERTIFICATES_ERROR",
      flush: true,
      header: /*#__PURE__*/_jsx(MostPopularCertificatesHeader, {
        numCompletedCertificates: numCompletedCertificates || 0
      }),
      headerActions: /*#__PURE__*/_jsx(RouterLink, {
        to: getSearchUrl({
          showOnlyCerts: true
        }),
        underline: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.mostPopularCertificates.showAllCertificates"
        })
      }),
      isLoading: certificatesLoading,
      navMarker: "HOMEPAGE_POPULAR_CERTIFICATES_LOADED",
      onClickNext: handleClickNext,
      onClickPrevious: handleClickPrevious,
      onItemClick: handleOnItemClick,
      setMaxCards: setMaxCards,
      showNext: hasMore,
      showPrevious: showPrevious,
      size: isTablet ? 'small' : 'large'
    })
  });
};
export default MostPopularCertificates;